import Phaser from 'phaser';
import PlayerCharacter from '../components/player/PlayerCharacter';
import { loadSceneSprites, createSceneAnimations, loadMobSprites, createMobAnimations } from '../util/Animations';
import { createMob, MobSpawner, ZombieMob, DummyMob, BombZombieMob, SkeletonKnightBoss } from '../components/mob';
import { DialogueWindow } from '../components/ui/DialoguePanel';
import WeaponUI from '../components/ui/WeaponUI';
import WeaponSelectionUI from '../components/ui/WeaponSelectionUI';
import { itemManager } from '../managers/ItemManager';


export default class Combat1Scene extends Phaser.Scene {
    constructor() {
        super({ key: 'Combat1Scene' });
        
        // Combat state
        this.bossTriggered = false;
        this.bossFight = false;
        this.movementLocked = false;
        this.uiInteractionBlocked = false; // Prevents weapon/tool actions during UI interactions
        
        // Scene objects
        this.bricks = [];
        this.healingFont = null;
        this.healingFontUsed = false;
        this.chainsParallax = null;
        
        // Boss fight effects
        this.bloodDrops = [];
        this.bossText = null;
        this.boss = null;
        
        // Test mobs for AI demonstration
        this.testMobs = [];
        this.mobSpawners = [];
        
        // Audio
        this.bgMusic = null;
        
        // Weapon system state
        this.currentWeaponSprite = null;
        this.bowDrawing = false;
        this.bowDrawn = false;
        this.bowSprite = null;
        this.arrowSprite = null;
        this.bowDrawStartTime = 0;
        this.bowDrawDuration = 500; // 1/2 second to draw
        this.playerMovementBlocked = false;
        this.playerCanAttack = true;
        this.cooldownTimer = 0;
    }

    preload() {
        // All assets are already loaded by LoadingScene
        // No need to reload anything here
    }

    async create(data = {}) {
        // Setup camera viewport
        this.cameras.main.setViewport(0, 0, 256, 192);
        
        // Disable browser context menu
        this.input.mouse.disableContextMenu();
        
        // Create player animations and mob animations
        PlayerCharacter.createAnimations(this);
        createSceneAnimations(this);
        createMobAnimations(this);
        
        // Get user's preferred color
        const playerColor = await PlayerCharacter.getUserColor(this);
        
        // Setup camera with world bounds (1024x192)
        this.cameras.main.setBounds(0, 0, 1024, 192);
        this.cameras.main.setZoom(1);
        
        // Load background
        this.add.image(512, 96, 'combat_scene_1').setDepth(0);
        
        // Setup player
        const spawnPosition = data.spawnPoint ? { x: 50, y: 96 } : { x: 50, y: 96 };
        this.player = new PlayerCharacter(this, spawnPosition.x, spawnPosition.y, playerColor);
        this.player.setDepth(10);
        this.player.playColorAnimation("idle2", true);
        
        // Camera follows player
        this.cameras.main.startFollow(this.player);
        
        // Setup weapon UI
        this.setupWeaponUI();
        this.setupCooldownTimer();

        // Setup audio
        this.setupAudio();
        
        // Setup physics world
        this.setupPhysics();
        
        // Create scene objects
        this.createBricks();
        this.createHealingFont();
        this.createChainsParallax();
        
        // Setup collision detection
        this.setupCollision();
        
        // Setup boss fight trigger
        this.setupBossFightTrigger();
        
        // Create test mobs for AI demonstration
        this.createTestMobs();
        
        // Setup input handlers
        this.setupInputHandlers();
    }

    update(time, deltaTime) {
        // Debug check: if movement is blocked but no bow sprites exist, restore movement
        if (this.playerMovementBlocked && !this.bowSprite && !this.arrowSprite && !this.bowDrawing && !this.bowDrawn) {
            console.warn('Movement was blocked but bow state is clean - restoring movement');
            this.playerMovementBlocked = false;
        }
        
        // Update player (block movement if drawing bow)
        if (this.player && this.player.update && !this.playerMovementBlocked) {
            this.player.update(deltaTime);
        }

        // Update weapon UI
        if (this.weaponUI && this.weaponUI.update) {
            this.weaponUI.update(deltaTime);
        }

        // Update all test mobs
        if (this.testMobs) {
            this.testMobs.forEach(mob => {
                if (mob && mob.update) {
                    mob.update(deltaTime);
                }
            });
        }

        // Update all spawners
        if (this.mobSpawners) {
            this.mobSpawners.forEach(spawner => {
                if (spawner && spawner.update) {
                    spawner.update(deltaTime);
                }
                
                // Update all spawned mobs and ensure they have collision detection
                if (spawner.spawnedMobs && spawner.spawnedMobs.length > 0) {
                    spawner.spawnedMobs.forEach((mob, index) => {
                        if (mob && mob.update) {
                            mob.update(deltaTime);
                            
                            // Add collision detection if not already added
                            if (mob.sprite && !mob.sprite.hasPlayerCollision) {
                                this.physics.add.collider(this.player, mob.sprite);
                                mob.sprite.hasPlayerCollision = true; // Mark to prevent duplicate collision setup
                            }
                        } else {
                            console.warn(`Spawned mob ${index} has no update method:`, mob);
                        }
                    });
                }
            });
        }

        // Update boss if it exists
        if (this.boss && this.boss.update) {
            this.boss.update(deltaTime);
        }

        // Update parallax effect
        if (this.chainsParallax) {
            this.chainsParallax.x = -this.cameras.main.scrollX * 0.1;
        }
    }
    
    setupWeaponUI() {
        // Create weapon UI
        this.weaponUI = new WeaponUI(this, 0, 0);
        
        // Create weapon selection UI
        this.weaponSelectionUI = new WeaponSelectionUI(this, 0, 0);
        
        //console.log('Weapon UI systems initialized');
    }

    setupCooldownTimer() {
        this.cooldownTimer = this.add.sprite(this.weaponUIX, this.weaponUIY + (this.weaponUI.height / 12) + 8,  'cooldown_timer');
        this.cooldownTimer.setDepth(508);
    }
    
    setupAudio() {
        // Stop any existing audio
        this.sound.stopAll();
        
        // Check if dungeonBgm1 exists in cache
        if (!this.cache.audio.exists('dungeonBgm1')) {
            console.warn('dungeonBgm1 audio not found in cache');
            return;
        }

        if (!this.cache.audio.exists('healing-font')) {
            console.warn('healing-font audio not found in cache');
            return;
        }
        
        // Create and play dungeon background music
        this.bgMusic = this.sound.add('dungeonBgm1', {
            volume: 0.6,
            loop: true
        });
        
        this.bgMusic.play();
    }
    
    setupPhysics() {
        // Set world bounds
        this.physics.world.setBounds(0, 0, 1024, 192);
        this.player.body.setCollideWorldBounds(true);
        
        // Create collision groups
        this.collisionBodies = this.physics.add.staticGroup();
        
        // Add collision rectangles for scene boundaries
        this.createCollisionAreas();
    }
    
    createCollisionAreas() {
        // Define collision rectangles for walls and boundaries
        const collisionAreas = [
            { x: 512, y: 13, width: 1024, height: 26 }, // Upper wall
        ];
        
        // Create collision rectangles
        collisionAreas.forEach(area => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0xFF0000, 0);
            this.physics.add.existing(rectangle, true); // true makes it static
            this.collisionBodies.add(rectangle);
        });
        
        //console.log(`Created ${collisionAreas.length} collision areas`);
    }
    
    createBricks() {
        const brickTypes = ['bricks_a', 'bricks_b', 'bricks_c', 'bricks_d'];
        const centerX = 512; // Middle of 1024px scene
        const centerY = 96;  // Middle of 192px scene
        
        // Create 4 bricks in a 2x2 formation
        const positions = [
            { x: centerX - 20, y: centerY - 20 }, // Top-left
            { x: centerX + 20, y: centerY - 20 }, // Top-right
            { x: centerX - 20, y: centerY + 20 }, // Bottom-left
            { x: centerX + 20, y: centerY + 20 }  // Bottom-right
        ];
        
        for (let i = 0; i < 4; i++) {
            const brick = this.add.image(positions[i].x, positions[i].y, brickTypes[i]);
            brick.setDepth(5);
            
            // Enable physics and add tight collision bounds
            this.physics.add.existing(brick, true); // true = static body
            brick.body.setSize(30, 30); // Tight collision bounds (30x30 instead of 32x32)
            brick.body.setOffset(1, 1); // Center the collision box
            
            this.collisionBodies.add(brick);
            this.bricks.push(brick);
        }        
    }
    
    createHealingFont() {
        // Place healing font on the left side of the scene
        this.healingFont = this.add.sprite(672, 48, 'healing_font');
        this.healingFont.setDepth(8);
        
        // Enable physics for collision detection
        this.physics.add.existing(this.healingFont, true); // true = static body
        this.healingFont.body.setSize(28, 28); // Collision area
        this.healingFont.body.setOffset(2, 2);        
    }
    
    createChainsParallax() {
        // Create chains at highest depth (above player)
        this.chainsParallax = this.add.image(512, 96, 'chains_parallax');
        this.chainsParallax.setDepth(15); // Above everything
        this.chainsParallax.setAlpha(0.8); // Slightly transparent for atmosphere
        this.chainsParallax.setScrollFactor(0.3); // Parallax effect (slower than camera)
    }
    
    setupCollision() {
        // Player collision with bricks
        this.physics.add.collider(this.player, this.collisionBodies);
        
        // Player overlap with healing font
        this.physics.add.overlap(this.player, this.healingFont, () => {
            this.onPlayerHealingFont();
        });
        
        // Player collision with mobs to prevent overlapping (added to update method)
        // Note: Mob collision will be set up dynamically as mobs are created
    }
    
    onPlayerHealingFont() {
        if (this.healingFontUsed) return;
        
        this.healingFontUsed = true;
        
        // Heal player to max HP
        this.player.modifyHealth(100, true); // Set to max HP
        this.player.modifyEnergy(100, true);
        this.player.modifyHunger(7, true)
        // Change sprite to empty version
        this.healingFont.setTexture('healing_font_empty');
        this.healingFont.stop(); // Stop animation
        

        // play sound
        this.sound.play('healing-font', { volume: 0.4 });


        // Visual effect
        const healText = this.add.text(this.healingFont.x, this.healingFont.y - 30, '+100 HP', {
            fontSize: '16px',
            fill: '#00ff00',
            stroke: '#009900',
            strokeThickness: 2
        });
        healText.setOrigin(0.5);
        healText.setDepth(20);
        
        // Animate heal text
        this.tweens.add({
            targets: healText,
            y: healText.y - 20,
            alpha: 0,
            duration: 1500,
            onComplete: () => healText.destroy()
        });

        const energyText = this.add.text(this.healingFont.x, this.healingFont.y - 40, '+100 Energy', {
            fontSize: '16px',
            fill: '#009999',
            stroke: '#000888',
            strokeThickness: 2
        });
        healText.setOrigin(0.5);
        healText.setDepth(20);
        
        // Animate heal text
        this.tweens.add({
            targets: healText,
            y: healText.y - 20,
            alpha: 0,
            duration: 1500,
            onComplete: () => healText.destroy()
        });

        const hungerText = this.add.text(this.healingFont.x, this.healingFont.y - 50, '+7 Hunger', {
            fontSize: '16px',
            fill: '#ffff00',
            stroke: '#ff9900',
            strokeThickness: 2
        });
        healText.setOrigin(0.5);
        healText.setDepth(20);
        
        // Animate heal text
        this.tweens.add({
            targets: healText,
            y: healText.y - 20,
            alpha: 0,
            duration: 1500,
            onComplete: () => healText.destroy()
        });
        
    }
    
    setupBossFightTrigger() {
        // Create invisible trigger zone at the right end of the scene
        const triggerZone = this.add.zone(1024 - 128, 96, 200, 192); // Right 256px of scene
        this.physics.add.existing(triggerZone);
        
        // Setup overlap detection
        this.physics.add.overlap(this.player, triggerZone, () => {
            if (!this.bossTriggered) {
                this.triggerBossFight();
            }
        });
    }
    
    triggerBossFight() {
        if (this.bossTriggered) return;
        
        this.bossTriggered = true;
        this.bossFight = true;
                
        // Lock player movement to the right (prevent going back)
        this.setupMovementRestriction();
        
        // Start boss fight sequence
        this.startBossFightSequence();
    }
    
    setupMovementRestriction() {
        // Create invisible wall to prevent player from going back
        const wall = this.add.rectangle(1024 - 256, 96, 10, 192, 0xff0000, 0);
        this.physics.add.existing(wall, true);
        this.physics.add.collider(this.player, wall);
    }
    
    startBossFightSequence() {
        // Clear all existing enemies before starting boss fight
        this.clearAllEnemies();
        
        // 1. Flicker scene black
        this.flickerScene(() => {
            // 2. Add blood drops overlay
            this.addBloodDropsOverlay(() => {
                // 3. Show boss text and spawn boss
                this.showBossText(() => {
                    this.spawnBoss();
                });
            });
        });
    }
    
    clearAllEnemies() {
        // Clear test mobs
        if (this.testMobs && this.testMobs.length > 0) {
            this.testMobs.forEach(mob => {
                if (mob && mob.destroy) {
                    mob.destroy();
                }
            });
            this.testMobs = [];
        }
        
        // Clear mob spawners and their spawned mobs
        if (this.mobSpawners && this.mobSpawners.length > 0) {
            this.mobSpawners.forEach(spawner => {
                if (spawner) {
                    // Clear all mobs spawned by this spawner
                    if (spawner.spawnedMobs && spawner.spawnedMobs.length > 0) {
                        spawner.spawnedMobs.forEach(mob => {
                            if (mob && mob.destroy) {
                                mob.destroy();
                            }
                        });
                        spawner.spawnedMobs = [];
                    }
                    
                    // Destroy the spawner itself
                    if (spawner.destroy) {
                        spawner.destroy();
                    }
                }
            });
            this.mobSpawners = [];
        }
    }
    
    flickerScene(callback) {
        let flickerCount = 0;
        const maxFlickers = 6;
        
        const flicker = () => {
            flickerCount++;
            
            // Flash black
            this.cameras.main.flash(100, 0, 0, 0);
            
            if (flickerCount < maxFlickers) {
                this.time.delayedCall(150, flicker);
            } else if (callback) {
                callback();
            }
        };
        
        flicker();
    }
    
    addBloodDropsOverlay(callback) {
        const bloodDropsLocations = [ 
            {x: 769 , y: 68 },
            {x: 815 , y: 96 },
            {x: 832 , y: 78 },
            {x: 864 , y: 92 },
            {x: 896 , y: 64 },
            {x: 933 , y: 96 },
            {x: 950 , y: 64 },
            {x: 988 , y: 90 },
            {x: 1020 , y: 64 },
            {x: 772 , y: 128 },
            {x: 804 , y: 160 },
            {x: 832 , y: 133 },
            {x: 860 , y: 160 },
            {x: 896 , y: 140 },
            {x: 925 , y: 160 },
            {x: 960 , y: 128 },
            {x: 988 , y: 150 },
            {x: 1024 , y: 128 }
        ];

        
        for (let i = 0; i < bloodDropsLocations.length; i++) {
            const x = bloodDropsLocations[i].x;
            const y = bloodDropsLocations[i].y;
            
            const bloodDrop = this.add.sprite(x, y, 'blood_drops');
            bloodDrop.setDepth(18); // Below chains but above everything else
            bloodDrop.play('blood_drops_dripping');
            bloodDrop.setAlpha(0.7);
            
            this.bloodDrops.push(bloodDrop);
        }
        
        if (callback) {
            this.time.delayedCall(1000, callback);
        }
    }
    
    showBossText(callback) {
        // Create boss text overlay at bottom of screen
        this.bossText = this.add.text(512, 160, 'SKELETON KNIGHT', {
            fontSize: '24px',
            fill: '#ff0000',
            stroke: '#000000',
            strokeThickness: 3,
            fontStyle: 'bold'
        });
        this.bossText.setOrigin(0.5);
        this.bossText.setDepth(20);
        this.bossText.setScrollFactor(0); // Fixed to camera
        
        // Animate text appearance
        this.bossText.setAlpha(0);
        this.tweens.add({
            targets: this.bossText,
            alpha: 1,
            duration: 1000,
            onComplete: () => {
                if (callback) {
                    this.time.delayedCall(2000, callback);
                }
            }
        });
    }
    
    spawnBoss() {
        const dialogue = DialogueWindow.createCameraRelative(this, 10, 10);
        dialogue.setMultiDialogue([
            ["You shouldn't have come here", "skeleton", "skeleton-knight-1"],
            ["PREPARE TO DIE!!!", "skeleton2", "skeleton-knight-2"],
        ]);
        
        // Spawn skeleton knight boss at a more centered position for better visibility
        this.boss = createMob(this, 800, 96, 'skeleton_knight', {
            health: 200,
            damage: 20,
            speed: 50
        });
        
        if (this.boss) {
            
            // Ensure boss sprite is visible and at high depth
            if (this.boss.sprite) {
                this.boss.sprite.setDepth(15); // Higher depth to ensure visibility
                this.boss.sprite.setVisible(true);
                this.boss.sprite.setAlpha(1);
                
                // Add collision detection between boss and player to prevent overlapping
                this.physics.add.collider(this.player, this.boss.sprite);
            }
        } else {
            console.error('Failed to create skeleton knight boss!');
        }
                
        // Listen for boss death
        this.events.on('mobDied', (mob) => {
            if (mob === this.boss) {
                this.onBossDefeated();
            }
        });
        
        // Fade out boss text after boss appears
        this.time.delayedCall(3000, () => {
            if (this.bossText) {
                this.tweens.add({
                    targets: this.bossText,
                    alpha: 0,
                    duration: 1000,
                    onComplete: () => this.bossText.destroy()
                });
            }
        });
    }
    
    onBossDefeated() {
        
        // Clean up blood drops
        this.bloodDrops.forEach(drop => drop.destroy());
        this.bloodDrops = [];
        
        // Show victory text
        const victoryText = this.add.text(512, 96, 'VICTORY!', {
            fontSize: '32px',
            fill: '#00ff00',
            stroke: '#000000',
            strokeThickness: 4,
            fontStyle: 'bold'
        });
        victoryText.setOrigin(0.5);
        victoryText.setDepth(20);
        victoryText.setScrollFactor(0);
        
        // Flash victory text
        this.tweens.add({
            targets: victoryText,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 500,
            yoyo: true,
            repeat: -1
        });
    }
    
    createTestMobs() {
        // Create some test mobs for AI demonstration (before boss fight)
        const testMobConfigs = [
            { type: 'zombie', x: 300, y: 96 }
        ];
        
        testMobConfigs.forEach(config => {
            //console.log(`Creating test mob: ${config.type} at (${config.x}, ${config.y})`);
            const mob = createMob(this, config.x, config.y, config.type);
            if (mob) {
                //console.log(`Test mob created successfully: ${mob.mobType} at (${Math.round(mob.x)}, ${Math.round(mob.y)})`);
                this.testMobs.push(mob);
                
                // Add collision detection between mob and player to prevent overlapping
                this.physics.add.collider(this.player, mob.sprite);
            }
        });
        
        // Create a mob spawner for additional enemies
        //console.log(`Creating MobSpawner for jump_zombie at (400, 96)`);
        /* const spawner = new MobSpawner(this, 400, 96, {
            mobType: 'jump_zombie',
            spawnInterval: 3000, // 3 seconds for testing
            maxMobs: 2,
            spawnRadius: 30
        });
        this.mobSpawners.push(spawner);
        console.log(`MobSpawner created and added to array. Total spawners: ${this.mobSpawners.length}`);
         */
    }
    
    setupInputHandlers() {
        // ESC key to return to main scene (for testing) or cancel bow drawing
        this.input.keyboard.on('keydown-ESC', () => {
            // If bow is being drawn, cancel it
            if (this.bowDrawing || this.bowDrawn) {
                this.cleanupBowState();
            } else {
                this.scene.start('MainScene');
            }
        });
        
        // R key to reset combat scene
        this.input.keyboard.on('keydown-R', () => {
            this.scene.restart();
        });
        
        // Debug key to spawn random mob
        this.input.keyboard.on('keydown-T', () => {
            const mobTypes = ['zombie', 'skeleton_archer', 'jump_zombie', 'poison_zombie', 'tongue_zombie'];
            const randomType = mobTypes[Math.floor(Math.random() * mobTypes.length)];
            const mob = createMob(this, this.player.x + 100, this.player.y, randomType);
            if (mob) {
                this.testMobs.push(mob);
            } else {
                console.warn(`Failed to spawn mob: ${randomType}`);
            }
        });
        
        // Setup click handlers for weapon usage
        this.input.on('pointerdown', (pointer) => {
            if (this.playerCanAttack) {
                this.handleWeaponClick(pointer);
            } 
        });
        
        // Setup pointer up for ranged weapon release
        this.input.on('pointerup', (pointer) => {
            this.handleWeaponRelease(pointer);
        });
        
        // Track mouse movement for bow aiming
        this.input.on('pointermove', (pointer) => {
            this.handleMouseMove(pointer);
        });
    }
    
    isPointerOverUI(pointer) {
        // Check if pointer is over any UI elements
        const screenX = pointer.x;
        const screenY = pointer.y;
        
        // Check WeaponUI areas
        if (this.weaponUI && this.weaponUI.slot1Area && this.weaponUI.slot2Area) {
            const slot1Bounds = this.weaponUI.slot1Area.getBounds();
            const slot2Bounds = this.weaponUI.slot2Area.getBounds();
            
            if (slot1Bounds.contains(screenX, screenY) || slot2Bounds.contains(screenX, screenY)) {
                return true;
            }
        }
        
        // Check WeaponSelectionUI (if visible)
        if (this.weaponSelectionUI && this.weaponSelectionUI.isVisible) {
            const weaponSelectionBounds = {
                x: this.weaponSelectionUI.x - 128,
                y: this.weaponSelectionUI.y - 96,
                width: 256,
                height: 192
            };
            
            if (screenX >= weaponSelectionBounds.x && 
                screenX <= weaponSelectionBounds.x + weaponSelectionBounds.width &&
                screenY >= weaponSelectionBounds.y && 
                screenY <= weaponSelectionBounds.y + weaponSelectionBounds.height) {
                return true;
            }
        }
        
        return false;
    }
    
    handleWeaponClick(pointer) {
        // If bow is drawing/drawn and user clicks with left button, cancel bow
        if ((this.bowDrawing || this.bowDrawn) && pointer.leftButtonDown()) {
            this.cleanupBowState();
            return;
        }
        
        // Prevent weapon use during weapon animation or bow drawing
        if (this.currentWeaponSprite || this.bowDrawing || this.bowDrawn) return;
        
        // Prevent weapon use if UI interaction blocking flag is set
        if (this.uiInteractionBlocked) {
            this.uiInteractionBlocked = false;
            return;
        }
        
        // Prevent weapon use if weapon selection UI is open
        if (this.weaponSelectionUI && this.weaponSelectionUI.isVisible) {
            return;
        }
        
        // Prevent weapon use if clicking on UI elements
        if (this.isPointerOverUI(pointer)) {
            return;
        }
        
        // Get equipped weapons
        const equippedWeapons = this.weaponUI ? this.weaponUI.getEquippedWeapons() : { slot1: null, slot2: null };
        let selectedWeapon = null;
        
        if (pointer.leftButtonDown() && equippedWeapons.slot1) {
            selectedWeapon = equippedWeapons.slot1;
            this.useWeapon(1, selectedWeapon, pointer);
        } else if (pointer.rightButtonDown() && equippedWeapons.slot2) {
            pointer.event.preventDefault(); // Prevent browser context menu
            selectedWeapon = equippedWeapons.slot2;
            this.useWeapon(2, selectedWeapon, pointer);
        }
    }
    
    handleWeaponRelease(pointer) {
        // Handle right mouse button release for ranged weapons
        if (pointer.rightButtonReleased() && (this.bowDrawing || this.bowDrawn)) {
            this.fireRangedWeapon();
        }
    }
    
    handleMouseMove(pointer) {
        // Update bow aiming direction if bow is drawn or drawing
        if ((this.bowDrawing || this.bowDrawn) && this.bowSprite && this.arrowSprite) {
            this.updateBowAiming(pointer);
        }
    }
    
    useWeapon(slotNumber, weaponId, pointer) {
        // Get weapon data
        const weaponData = itemManager.getItemData(weaponId);
        if (!weaponData) {
            return;
        }
        
        // Check cooldown (if implemented)
        // const cooldownTime = slotNumber === 1 ? this.weaponUI.slot1Cooldown : this.weaponUI.slot2Cooldown;
        // if (cooldownTime > 0) return;
        
        // Use weapon based on subtype
        if (weaponData.subtype === 'Melee') {
            this.useMeleeWeapon(weaponData, pointer);
        } else if (weaponData.subtype === 'Ranged') {
            this.startDrawingBow(weaponData, pointer);
        }
        
    }
    
    useMeleeWeapon(weaponData, pointer) {
        const player = this.player;
        if (!player) return;
        
        // Show weapon sprite swinging
        this.showMeleeWeaponSprite(player.lastDirection, weaponData.textureKey);
        this.playerCanAttack = false
        setTimeout(() => {
           this.playerCanAttack = true;
        }, weaponData.speed * 1000);
        
        // Create visual effect in front of player (commented out for now, might use later)
        /*
        const direction = player.lastDirection || 'down';
        let effectX = player.x;
        let effectY = player.y;
        
        // Position effect in front of player based on direction
        switch (direction) {
            case 'up': effectY -= 30; break;
            case 'down': effectY += 30; break;
            case 'left': effectX -= 30; break;
            case 'right': effectX += 30; break;
        }
        
        // Create swing arc effect
        const arcGraphics = this.add.graphics();
        arcGraphics.lineStyle(3, 0xffffff, 0.8);
        arcGraphics.beginPath();
        arcGraphics.arc(effectX, effectY, 25, 0, Math.PI, false);
        arcGraphics.strokePath();
        
        // Animate and destroy the arc
        this.tweens.add({
            targets: arcGraphics,
            alpha: 0,
            duration: 200,
            onComplete: () => arcGraphics.destroy()
        });
        */
        
        // Check for mob hits in range
        const range = 40; // Melee range
        this.checkMobHits(player.x, player.y, range, weaponData.damage || 10);
    }
    
    /* showMeleeWeaponSprite(weaponData) {
        if (this.currentWeaponSprite) return; // Animation already in progress
        
        const player = this.player;
        const direction = player.lastDirection || 'down';
        
        // Create weapon sprite
        const textureKey = weaponData.textureKey;
        if (!textureKey || !this.textures.exists(textureKey)) {
            console.warn('Weapon texture not found:', textureKey);
            return;
        }
        
        // Position weapon relative to player based on direction
        let weaponX = player.x;
        let weaponY = player.y;
        let startRotation = 0;
        let endRotation = 0;
        
        switch (direction) {
            case 'up':
                weaponY -= 20;
                startRotation = -Math.PI / 4; // -45 degrees
                endRotation = -3 * Math.PI / 4; // -135 degrees
                break;
            case 'down':
                weaponY += 20;
                startRotation = Math.PI / 4; // 45 degrees
                endRotation = 3 * Math.PI / 4; // 135 degrees
                break;
            case 'left':
                weaponX -= 20;
                startRotation = Math.PI; // 180 degrees
                endRotation = Math.PI / 2; // 90 degrees
                break;
            case 'right':
                weaponX += 20;
                startRotation = 0; // 0 degrees
                endRotation = Math.PI / 2; // 90 degrees
                break;
        }
        
        // Create weapon sprite
        this.currentWeaponSprite = this.add.image(weaponX, weaponY, textureKey);
        this.currentWeaponSprite.setDepth(6); // Above player
        this.currentWeaponSprite.setRotation(startRotation);
        this.currentWeaponSprite.setOrigin(0.5, 0.5); // Rotate around handle
        
        // Animate swing
        this.tweens.add({
            targets: this.currentWeaponSprite,
            rotation: endRotation,
            duration: 150,
            ease: 'Power2.easeInOut',
            onComplete: () => {
                if (this.currentWeaponSprite) {
                    this.currentWeaponSprite.destroy();
                    this.currentWeaponSprite = null;
                }
            }
        });
    } */

    showMeleeWeaponSprite(playerDirection, textureKey) {
        // Starting position for scythe (in front of player)
        let startX = this.player.x;
        let startY = this.player.y + 10;
        let startRotation = 0;
        let flipX = false;
        let waterX = 0;
        let waterY = 0;
        
        switch (playerDirection) {
            case 'up':
                startY -= 16;
                startRotation = 0;
                waterX = startX + 3;
                waterY = startY - 8;
                break;
            case 'down':
                startY += 16;
                startRotation = 0;
                waterX = startX + 3;
                waterY = startY + 8;
                break;
            case 'left':
                startX -= 16;
                startRotation = 0;
                flipX = true;
                waterX = startX - 5;
                waterY = startY + 5;
                break;
            case 'right':
                startX += 16;
                startRotation = 0;
                waterX = startX + 5;
                waterY = startY + 5;
                break;
        }
        
        // Create scythe sprite at starting position
        this.currentMeleeWeaponSprite = this.add.image(startX, startY, textureKey);
        this.currentMeleeWeaponSprite.setDepth(6); // Above player
        this.currentMeleeWeaponSprite.setRotation(startRotation);
        this.currentMeleeWeaponSprite.setFlipX(flipX);
        
        // Set origin for more natural rotation (closer to handle/player end)
        // For a 17x17 sprite, (0.5, 0.85) puts rotation point near bottom of handle
        this.currentMeleeWeaponSprite.setOrigin(0.5, 0.75);
        
        // Create water animation sprite
        const scytheSprite = this.add.sprite(waterX, waterY, 'tool-scythe', 0);
        scytheSprite.setDepth(6); // Same depth as tool
        scytheSprite.setOrigin(0, 0.5);
        scytheSprite.setVisible(false);
        
        // Phase 1: Rotate scythe 60 degrees
        const rotationDirection = playerDirection === 'left' ? -1 : 1;
        const targetRotation = startRotation + (Math.PI / 3) * rotationDirection;
        
        // Play random scythe swing sound effect
        const scytheSound = Math.random() < 0.5 ? 'sword-swing-1' : 'sword-swing-2';
        this.sound.play(scytheSound, { volume: 0.5 });
           
        this.tweens.add({
            targets: this.currentMeleeWeaponSprite,
            rotation: targetRotation,
            duration: 200,
            ease: 'Power2.easeOut',
            onComplete: () => {
                if (this.currentMeleeWeaponSprite) {
                    this.currentMeleeWeaponSprite.destroy();
                    this.currentMeleeWeaponSprite = null;
                }
            }
        });
    }
    
    startDrawingBow(weaponData, pointer) {
        if (this.bowDrawing || this.bowDrawn) return;
        
        this.bowDrawing = true;
        this.bowDrawStartTime = this.time.now;
        this.playerMovementBlocked = true;
        
        // Add timeout safeguard - auto-cancel after 10 seconds
        this.bowTimeoutTimer = this.time.delayedCall(10000, () => {
            this.cleanupBowState();
        });
        
        const player = this.player;
        
        // Create bow sprite
        const bowTextureKey = weaponData.textureKey;
        if (!bowTextureKey || !this.textures.exists(bowTextureKey)) {
            console.warn('Bow texture not found:', bowTextureKey);
            this.bowDrawing = false;
            this.playerMovementBlocked = false;
            return;
        }
        
        this.bowSprite = this.add.sprite(player.x + 10, player.y + 8 , bowTextureKey);
        this.bowSprite.setDepth(500);
        this.bowSprite.setOrigin(0.5, 0.5);
        this.bowSprite.setRotation(Math.PI / 4); // Bow artwork points NE, subtract 45° to point right
        
        // Create arrow sprite
        // Use default arrow texture or from weapon ammo data
        const ammoData = weaponData.ammo && weaponData.ammo['0'] ? weaponData.ammo['0'] : { textureKey: 'arrow' };
        this.arrowSprite = this.add.sprite(player.x + 10, player.y + 8, ammoData.textureKey || 'arrow');
        this.arrowSprite.setDepth(500); // Above bow
        this.arrowSprite.setOrigin(0.5, 0.5);
        this.arrowSprite.setScale(0.8);
        this.arrowSprite.setRotation(Math.PI / 4); // Arrow artwork points up, add 90° to point right
        
        // Start bow drawing animation (frames 0-5)
        this.bowSprite.setFrame(0);
        this.bowDrawAnimationTimer = this.time.addEvent({
            delay: this.bowDrawDuration / 6, // 6 frames over 1/2 second
            callback: this.updateBowDrawFrame,
            callbackScope: this,
            repeat: 5
        });
        
        // Initial aiming setup
        this.updateBowAiming(pointer);
    }
    
    updateBowDrawFrame() {
        if (!this.bowSprite) return;
        
        const currentFrame = this.bowSprite.frame.name;
        const nextFrame = Math.min(parseInt(currentFrame) + 1, 5);
        this.bowSprite.setFrame(nextFrame);
        
        // When fully drawn, set state
        if (nextFrame === 5) {
            this.bowDrawing = false;
            this.bowDrawn = true;
        }
    }
    
    updateBowAiming(pointer) {
        if (!this.bowSprite || !this.arrowSprite || !this.player) return;
        
        // Get world position of mouse
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        
        // Calculate angle from bow to mouse
        const angle = Phaser.Math.Angle.Between(
            this.bowSprite.x, this.bowSprite.y,
            worldPoint.x, worldPoint.y
        );
        
        // Apply different rotations based on each sprite's natural artwork orientation
        // Arrow artwork needs 135° clockwise adjustment to align properly
        const arrowRotation = angle + Math.PI / 4; // Add 45° (since -90° + 135° = 45°)
        
        // Bow artwork points northeast (45 degrees) by default, so subtract 45 degrees
        const bowRotation = angle +  Math.PI / 4; // Convert from mouse angle to bow's coordinate system
        
        // Rotate sprites to point at mouse
        this.bowSprite.setRotation(bowRotation);
        this.arrowSprite.setRotation(arrowRotation);
        
        // Update player facing direction based on mouse position
        const player = this.player;
        if (worldPoint.x < player.x - 10) {
            player.lastDirection = 'left';
        } else if (worldPoint.x > player.x + 10) {
            player.lastDirection = 'right';
        }
        
        if (worldPoint.y < player.y - 10) {
            player.lastDirection = 'up';
        } else if (worldPoint.y > player.y + 10) {
            player.lastDirection = 'down';
        }
    }
    
    fireRangedWeapon() {
        if (!this.bowSprite || !this.arrowSprite) return;
        
        const equippedWeapons = this.weaponUI ? this.weaponUI.getEquippedWeapons() : { slot1: null, slot2: null };
        const weaponData = itemManager.getItemData(equippedWeapons.slot2);
        if (!weaponData) return;
        
        // Store firing position before cleanup
        const firingX = this.arrowSprite.x;
        const firingY = this.arrowSprite.y;
        
        // Get current aim direction
        // Since bow artwork naturally points NE, add back the 45-degree offset to get actual direction
        const actualAngle = this.bowSprite.rotation - Math.PI / 4;
        const direction = {
            x: Math.cos(actualAngle),
            y: Math.sin(actualAngle)
        };
        
        // Create projectile arrow
        const ammoData = weaponData.ammo && weaponData.ammo['0'] ? weaponData.ammo['0'] : {
            textureKey: 'arrow',
            speed: 200,
            damage: 10
        };
        
        const projectile = this.physics.add.sprite(firingX, firingY, ammoData.textureKey || 'arrow');
        projectile.setScale(0.8);
        projectile.setRotation(actualAngle +  Math.PI / 4); // Arrow artwork points up, so subtract 90° from actual angle
        
        // Set velocity
        const speed = ammoData.speed || 200;
        projectile.setVelocity(direction.x * speed, direction.y * speed);
        
        // Set up collision and cleanup
        const maxRange = weaponData.range || 300;
        const startTime = this.time.now;
        const damage = (weaponData.damage || 10) + (ammoData.damage || 0);
        
        const updateProjectile = () => {
            if (!projectile.active) return;
            
            // Check range and time using stored firing position
            const distance = Phaser.Math.Distance.Between(
                firingX, firingY,
                projectile.x, projectile.y
            );
            
            if (distance > maxRange || this.time.now - startTime > 5000) {
                projectile.destroy();
                return;
            }
            
            // Check mob hits
            this.checkMobHits(projectile.x, projectile.y, 15, damage, () => {
                projectile.destroy();
            });
        };
        
        this.physics.world.on('worldstep', updateProjectile);
        projectile.once('destroy', () => {
            this.physics.world.off('worldstep', updateProjectile);
        });
        
        // Clean up bow and arrow sprites
        this.cleanupBowState();
        
        // Start cooldown timer
        this.playerCanAttack = false
        setTimeout(() => {
           this.playerCanAttack = true;
        }, weaponData.speed * 1000);
    }
    
    cleanupBowState() {        
        // Remove bow draw animation timer
        if (this.bowDrawAnimationTimer) {
            this.bowDrawAnimationTimer.remove();
            this.bowDrawAnimationTimer = null;
        }
        
        // Remove timeout timer
        if (this.bowTimeoutTimer) {
            this.bowTimeoutTimer.remove();
            this.bowTimeoutTimer = null;
        }
        
        // Remove bow and arrow sprites
        if (this.bowSprite) {
            this.bowSprite.destroy();
            this.bowSprite = null;
        }
        
        if (this.arrowSprite) {
            this.arrowSprite.destroy();
            this.arrowSprite = null;
        }
        
        // Reset state
        this.bowDrawing = false;
        this.bowDrawn = false;
        this.playerMovementBlocked = false;
        
    }
    
    checkMobHits(x, y, range, damage, callback) {
        // Check against test mobs
        if (this.testMobs) {
            this.testMobs.forEach(mob => {
                if (mob && mob.active && mob.sprite) {
                    const distance = Phaser.Math.Distance.Between(x, y, mob.sprite.x, mob.sprite.y);
                    if (distance <= range) {
                        mob.takeDamage(damage);
                        if (callback) callback();
                    }
                }
            });
        }
        
        // Check against spawned mobs
        if (this.mobSpawners) {
            this.mobSpawners.forEach(spawner => {
                if (spawner.spawnedMobs) {
                    spawner.spawnedMobs.forEach(mob => {
                        if (mob && mob.active && mob.sprite) {
                            const distance = Phaser.Math.Distance.Between(x, y, mob.sprite.x, mob.sprite.y);
                            if (distance <= range) {
                                mob.takeDamage(damage);
                                if (callback) callback();
                            }
                        }
                    });
                }
            });
        }
        
        // Check against boss
        if (this.boss && this.boss.active && this.boss.sprite) {
            const distance = Phaser.Math.Distance.Between(x, y, this.boss.sprite.x, this.boss.sprite.y);
            if (distance <= range) {
                this.boss.takeDamage(damage);
                if (callback) callback();
            }
        }
    }

    
    destroy() {
        // Stop background music when leaving scene
        if (this.bgMusic) {
            this.bgMusic.stop();
            this.bgMusic = null;
        }
        
        // Clean up weapon system
        if (this.currentWeaponSprite) {
            this.currentWeaponSprite.destroy();
            this.currentWeaponSprite = null;
        }
        
        if (this.currentToolSprite) {
            this.currentToolSprite.destroy();
            this.currentToolSprite = null;
        }
        
        this.cleanupBowState();
        
        super.destroy();
    }
} 